/**
 * ImportCallLogsModal
 *
 * Purpose:
 * - Allows importing Call Logs via CSV/XLS/XLSX file upload
 * - User maps CSV columns → required system fields (Duration, Contact ID, Owner, Created At)
 * - User can exclude specific rows before confirm import
 *
 * Component Flow:
 * 1. User drops or uploads a file
 * 2. File is parsed → columns + row data extracted (via handleFileUpload)
 * 3. User maps incoming CSV headers to system fields
 * 4. User selects rows to EXCLUDE (unchecked = include)
 * 5. On submit:
 *      - unmapped rows removed
 *      - mapping object { csvColumn: systemField } is built
 *      - mapCallLogs API is called to store processed logs
 *
 * Props:
 *  - open (boolean): modal visibility
 *  - close (function): closes modal
 *  - organization_id (string): ID used for mapping API
 *  - usersList (array): list of users needed for assigning logs
 *
 * External Services Used:
 *  - handleFileUpload → parse CSV/XLS
 *  - mapCallLogs → map & save uploaded call log rows
 */

import Modal from "react-modal";
import  { FunctionComponent, useEffect, useState } from "react";
import styles from "./ImportModal.module.css";
import { Checkbox } from "@material-ui/core";
import { IoIosClose, IoMdDocument } from "react-icons/io";
import { handleFileUpload } from "../../../Services/contacts";
import { useDispatch } from "react-redux";
import Loading from "../../Loading/Loading";
import { mapCallLogs } from "../../../Services/resources";

type props = {
  open: boolean;
  close: () => void;
  organization_id: string;
  usersList: any[];
};

const ImportCallLogsModal: FunctionComponent<props> = ({
  open,
  close,
  organization_id,
  usersList,
}) => {
  /** Parsed CSV column list */
  const [columns, setColumns] = useState<any[]>([]);

  /** Parsed CSV row data */
  const [data, setData] = useState<any[]>([]);

  /** List of excluded row indexes */
  const [select, setSelected] = useState<number[]>([]);

  /** Loading state */
  const [load, setLoad] = useState(false);

  /**
   * Mapping of system fields → CSV column name
   * Example:
   * { "Duration": "Call Duration", "Contact ID": "ID", ... }
   */
  const [colName, setColName] = useState<{ [key: string]: string }>({});

  /**
   * Available mapping fields.
   * Each field can be assigned to exactly one CSV column.
   */
  const [choice, setChoice] = useState([
    { name: "Duration", selected: false },
    { name: "Contact ID", selected: false },
    { name: "Owner", selected: false },
    { name: "Created At", selected: false },
  ]);

  /**
   * Update "choice.selected" whenever mapped CSV columns change
   */
  useEffect(() => {
    const columns = Object.values(colName);
    let tempChoices = [...choice];
    tempChoices.forEach((choice, index) => {
      if (columns.includes(choice.name)) {
        tempChoices[index].selected = true;
      } else {
        tempChoices[index].selected = false;
      }
    });

    setChoice(tempChoices);
    // eslint-disable-next-line
  }, [colName]);

  /**
   * Initialize colName state when columns are first loaded
   */
  useEffect(() => {
    let data: {} = {};
    columns.forEach((item: any, index: number) => {
      data = {
        ...data,
        [item.name]: "",
      };
    });
    setColName(data);
  }, [columns]);

  /**
   * Upload file handler
   * handleFileUpload returns:
   *  - parsed data rows
   *  - parsed column definitions
   */
  const upload = (file: any) => {
    handleFileUpload(
      file,
      (data: any) => setData(data),
      (col: any) => setColumns(col),
      (data: boolean) => setLoad(data)
    );
  };

  const dispatcher = useDispatch();

  /**
   * Final submit handler
   * Builds mapping object & removes excluded rows
   */
  const onSubmit = () => {
    setLoad(true);
    const map: { [key: string]: string } = {};
    Object.keys(colName).forEach((key) => {
      if (colName[key] !== "") map[colName[key]] = key;
    });

    let val: any[] = [...data];
    select.sort(function (a, b) {
      return b - a;
    });
    select.forEach((index) => {
      val.splice(index, 1);
    });

    mapCallLogs(
      val,
      map,
      organization_id,
      dispatcher,
      close,
      usersList,
      (data: boolean) => setLoad(data)
    );
  };

  /**
   * Called when user maps a column → system field
   */
  const onSelect = (item: any, value: string) => {
    let data: any = { ...colName };

    for (var key in data) {
      if (key === item.name) {
        data[key] = value === "Select" ? "" : value;
      }
    }
    setColName(data);
  };

  return (
    <Modal
      style={{ overlay: { zIndex: 100 } }}
      className={styles.parent}
      isOpen={open}
      onRequestClose={close}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
    >
      {load && <Loading />}

      {/* If a file is uploaded → show mapping and table preview */}
      {data.length !== 0 ? (
        <>
          <div className={styles.cross} onClick={close}>
            <IoIosClose size={35} color={"#808080"} />
          </div>

          <p className={styles.contactForm}>Import Data for "Call Logs Form"</p>

          {/* Preview Table */}
          <div className={styles.tableBox}>
            <table className={styles.table}>
              <thead className={styles.head}>
                <tr>
                  <th className={styles.th}></th>
                  <th className={styles.th}>Sno.</th>

                  {/* Column headers + Mapping dropdown */}
                  {columns.map((item: any, index: number) => (
                    <th key={index} className={styles.th}>
                      <div className={styles.tHeadContainer}>
                        <p>{item.name}</p>
                      </div>
                      <div className={styles.drop}>
                        <select
                          key={index}
                          className={styles.options}
                          onChange={(val) => onSelect(item, val.target.value)}
                        >
                          <option>Select</option>
                          {choice.map((option: any, idx: number) => (
                            <option disabled={option.selected} key={idx}>
                              {option.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </th>
                  ))}

                  <th className={styles.th}></th>
                </tr>
              </thead>

              <tbody>
                {data.map((row: any, index: number) => (
                  <tr
                    key={index}
                    className={styles.no}
                    style={
                      select.includes(index)
                        ? { backgroundColor: "#00000030" }
                        : {
                            backgroundColor: "#ffffff",
                            borderBottomColor: "#f3f3f3",
                            borderBottomWidth: "5px",
                          }
                    }
                  >
                    {/* Select/exclude row */}
                    <td>
                      <Checkbox
                        key={index}
                        checked={select.includes(index) ? false : true}
                        onChange={(e) => {
                          if (!e.target.checked) {
                            let data = [...select];
                            data.push(index);
                            setSelected(data);
                          } else if (e.target.checked) {
                            let data = [...select];
                            let item = select.indexOf(index);
                            if (item > -1) {
                              data.splice(item, 1);
                            }
                            setSelected(data);
                          }
                        }}
                      />
                    </td>

                    <td className={styles.td}>{index + 1}</td>

                    {/* Row values */}
                    {Object.values(row).map((val: any, i: number) => (
                      <td key={i} className={styles.td}>
                        {val}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Action Buttons */}
          <div className={styles.buttonBox}>
            <button className={styles.cancel} onClick={close}>
              Cancel
            </button>
            <button className={styles.confirm} onClick={onSubmit}>
              Confirm
            </button>
          </div>
        </>
      ) : (
        // Initial state: Drag/Drop or Upload
        <div className={styles.child}>
          <div className={styles.cross} onClick={close}>
            <IoIosClose size={35} color={"#808080"} />
          </div>

          <p className={styles.contactForm}>Import Data for "Call Logs Form"</p>

          {/* Drag Upload Box */}
          <div
            className={styles.dragBox}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              upload(e.dataTransfer.files[0]);
            }}
          >
            <IoMdDocument size={100} color={"#808080"} />
            <p>Drag Your Files Here</p>
          </div>

          {/* Upload Button */}
          <div className={styles.selectBox}>
            <label htmlFor="file-input" className={styles.uploadButton}>
              Upload File
            </label>

            <input
              id="file-input"
              className={styles.select}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e: any) => upload(e.target.files[0])}
            />
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ImportCallLogsModal;
